// In-listing variant selectors (plan T12) — colour / storage / … chips derived
// from the backend `selectors` (category_axis_roles). Pure + presentational so
// it renders in an RTL test without navigation/network. A listing with no axis
// rows (e.g. a single-config headphone) passes `selectors: []` and this renders
// nothing — no empty-selector UI.
//
// A one-value selector is not a choice, so it renders as plain `Label: value`
// text rather than a dead one-option chip. A `storage` selector whose only value
// is the retro-console placeholder (`Standard`) carries no information and is
// omitted (Wave 2a follow-ups T1). The API still returns both.
//
// A one-value selector need not apply to every variant (`build_selectors` skips a
// missing attribute: a Wii U card can hold a 32 GB variant beside variants of
// unknown storage). So when the caller passes the active variant's attributes, the
// text line renders only if that variant carries the value; otherwise it is omitted
// rather than presenting another variant's spec.
//
// It is still a choice when only some variants carry it: an earphone card whose
// variants differ only by `Wireless` vs. no connectivity recorded. So when the caller
// also passes every variant's attributes, a one-value selector that not every variant
// carries renders as a tappable chip (the old behaviour), and only one that every
// variant carries renders as text (PR #683 review).

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';
import type { CatalogSelector } from '../../lib/backendApi';

// The storage value a console with no storage axis groups under. Must equal
// `material_axes.FIXED_STORAGE_PLACEHOLDER` in the backend (pinned by
// backend/tests/test_fixed_storage_placeholder_frontend_pin.py).
export const FIXED_STORAGE_PLACEHOLDER = 'Standard';

function attributeMatches(
  attributes: Record<string, unknown> | undefined,
  axisKey: string,
  value: string
): boolean {
  if (attributes === undefined) return true;
  const own = attributes[axisKey];
  return own != null && String(own).trim() === value;
}

function coversEveryVariant(
  variantAttributes: Record<string, unknown>[] | undefined,
  axisKey: string,
  value: string
): boolean {
  if (variantAttributes === undefined) return true;
  return variantAttributes.every((attributes) => attributeMatches(attributes, axisKey, value));
}

function isPlaceholderOnly(selector: CatalogSelector): boolean {
  return (
    selector.axis_key === 'storage' &&
    selector.values.length === 1 &&
    selector.values[0] === FIXED_STORAGE_PLACEHOLDER
  );
}

interface Props {
  selectors: CatalogSelector[];
  // Currently-selected value per axis_key (controlled). Optional — when omitted
  // the chips render unselected (read-only display).
  selected?: Record<string, string>;
  onSelect?: (axisKey: string, value: string) => void;
  // The active variant's own attributes. Optional — when omitted, a one-value
  // selector always renders its value (read-only display).
  activeAttributes?: Record<string, unknown>;
  // Every variant's attributes. Optional — when given, a one-value selector that not
  // every variant carries stays a tappable chip, since it is a choice.
  variantAttributes?: Record<string, unknown>[];
}

export default function ListingSelectors({
  selectors,
  selected,
  onSelect,
  activeAttributes,
  variantAttributes,
}: Props) {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        block: { marginTop: theme.spacing.lg },
        axis: { marginBottom: theme.spacing.md },
        label: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          marginBottom: theme.spacing.sm,
        },
        chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
        chip: {
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusLg,
        },
        chipActive: { backgroundColor: theme.colors.accent, borderColor: theme.colors.accent },
        chipText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        chipTextActive: { color: theme.colors.backgroundPrimary },
        fixedLabel: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        },
        fixedValue: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
      }),
    [theme]
  );

  // A one-value selector is fixed text only when every variant carries it.
  const isFixed = (selector: CatalogSelector) =>
    selector.values.length === 1 &&
    coversEveryVariant(variantAttributes, selector.axis_key, selector.values[0]);
  const visible = selectors.filter(
    (selector) =>
      !isPlaceholderOnly(selector) &&
      (!isFixed(selector) ||
        attributeMatches(activeAttributes, selector.axis_key, selector.values[0]))
  );
  if (visible.length === 0) return null;

  return (
    <View style={styles.block}>
      {visible.map((selector) =>
        isFixed(selector) ? (
          <View key={selector.axis_key} style={styles.axis}>
            <Text>
              <Text style={styles.fixedLabel}>{selector.label}: </Text>
              <Text style={styles.fixedValue}>{selector.values[0]}</Text>
            </Text>
          </View>
        ) : (
          <View key={selector.axis_key} style={styles.axis}>
            <Text style={styles.label}>{selector.label}</Text>
            <View style={styles.chipRow}>
              {selector.values.map((value) => {
                const isActive = selected?.[selector.axis_key] === value;
                return (
                  <Pressable
                    key={value}
                    style={[styles.chip, isActive && styles.chipActive]}
                    onPress={() => onSelect?.(selector.axis_key, value)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: isActive }}
                  >
                    <Text style={[styles.chipText, isActive && styles.chipTextActive]}>
                      {value}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        )
      )}
    </View>
  );
}
